import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Account, Campaign } from '../../types/client-management.types';
import { Company } from 'src/pages/billing/types/billing.types';
import { portalDs } from 'src/components/portal-design-system';
import { Skeleton } from 'src/components/ui/skeleton';
import { cn } from 'src/lib/utils';
import { SelectionCheckbox } from './selection-checkbox';

interface ManageClientAccountCampaignSelectionProps {
  accounts: Account[];
  campaigns: Campaign[];
  companies?: Company[];
  selectedCompanies?: string[];
  selectedAccountIds: string[];
  selectedCampaignIds: string[];
  onSelectionChange: (accountIds: string[], campaignIds: string[]) => void;
  showCompanyGrouping?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  searchValue?: string;
  accountFilter?: string[];
  companyFilter?: string[];
}

type CheckboxState = 'checked' | 'unchecked' | 'indeterminate';

interface TreeNode {
  id: string;
  label: string;
  type: 'company' | 'account' | 'campaign';
  source?: string;
  children?: TreeNode[];
  accountId?: string; // For campaigns, track which account they belong to
  companyId?: string; // For accounts, track which company they belong to
  index?: number;
}

const ManageClientAccountCampaignSelection: React.FC<ManageClientAccountCampaignSelectionProps> = ({
  searchValue = '',
  accounts,
  campaigns,
  companies = [],
  selectedCompanies = [],
  selectedAccountIds,
  selectedCampaignIds,
  onSelectionChange,
  showCompanyGrouping = false,
  disabled = false,
  isLoading = false,
  accountFilter = [],
  companyFilter = [],
}) => {

  const [localStartingAccountIds, setLocalStartingAccountIds] = useState<string[]>(selectedAccountIds);
  const [localStartingCampaignIds, setLocalStartingCampaignIds] = useState<string[]>(selectedCampaignIds);
  const [localSelectedAccountIds, setLocalSelectedAccountIds] = useState<string[]>(selectedAccountIds);
  const [localSelectedCampaignIds, setLocalSelectedCampaignIds] = useState<string[]>(selectedCampaignIds);
  const [localSelectedCompanies, setLocalSelectedCompanies] = useState<string[]>(selectedCompanies);

  const [expandedCompanyIds, setExpandedCompanyIds] = useState<string[]>([]);

  // Sync with external props
  useEffect(() => {
    setLocalSelectedCompanies(selectedCompanies);
    setLocalStartingAccountIds(selectedAccountIds);
    setLocalStartingCampaignIds(selectedCampaignIds);
    setLocalSelectedAccountIds(selectedAccountIds);
    setLocalSelectedCampaignIds(selectedCampaignIds);
  }, [selectedAccountIds, selectedCampaignIds]);

  // Helper function to check if a label matches the search value
  const matchesSearch = (label: string): boolean => {
    if (!searchValue || searchValue.trim() === '') return true;
    return label.toLowerCase().includes(searchValue.toLowerCase().trim());
  };

  // Filter tree nodes based on search (only campaigns are filtered)
  const filterTreeNodes = (nodes: TreeNode[]): TreeNode[] => {
    if (!searchValue || searchValue.trim() === '') {
      return nodes;
    }

    return nodes
      .map(node => {
        if (node.type === 'company') {
          // Filter children (accounts) - only include company if it has matching campaigns
          const filteredChildren = filterTreeNodes(node.children || []);

          // Include company only if any of its children (accounts with matching campaigns) match
          if (filteredChildren.length > 0) {
            return {
              ...node,
              children: filteredChildren,
            };
          }
          return null;
        }

        if (node.type === 'account') {
          // Filter children (campaigns) - only filter campaigns, not the account itself
          const filteredChildren = (node.children || []).filter(child =>
            matchesSearch(child.label)
          );

          // Include account only if any of its campaigns match
          if (filteredChildren.length > 0) {
            return {
              ...node,
              children: filteredChildren,
            };
          }
          return null;
        }

        // For campaigns, check if they match the search
        return matchesSearch(node.label) ? node : null;
      })
      .filter((node): node is TreeNode => node !== null);
  };

  // Build tree structure
  const treeData = useMemo(() => {
    let builtTree: TreeNode[];

    // Apply filters
    let filteredCompanies = companies;
    if (companyFilter.length > 0) {
      filteredCompanies = companies.filter(company => companyFilter.includes(company.id));
    }

    let filteredAccounts = accounts;
    if (accountFilter.length > 0) {
      filteredAccounts = accounts.filter(account => accountFilter.includes(account.id));
    }

    // Filter campaigns to only include those from filtered accounts
    const filteredAccountIds = new Set(filteredAccounts.map(acc => acc.id));
    const filteredCampaigns = campaigns.filter((campaign) => (
      Boolean(campaign?.account?.id) && filteredAccountIds.has(campaign.account.id)
    ));

    if (showCompanyGrouping && filteredCompanies.length > 0) {
      // Group by company -> account -> campaign
      const companyMap = new Map<string, TreeNode>();

      const buildAccountNodes = (companyAccounts: Account[], index: number): TreeNode[] => (
        companyAccounts.map((account) => {
          const accountCampaigns = filteredCampaigns.filter(
            (camp) => camp.account?.id === account.id
          );
          return {
            id: account.ext_account_id,
            label: account.title,
            type: 'account' as const,
            source: account.source,
            companyId: account.company ?? undefined,
            index,
            children: accountCampaigns.map((campaign) => ({
              id: campaign.ext_campaign_id,
              label: campaign.title,
              type: 'campaign' as const,
              source: account.source,
              accountId: account.ext_account_id,
              index,
            })),
          };
        }).filter((accountNode) => (accountNode.children?.length ?? 0) > 0)
      );

      filteredCompanies.forEach((company, index) => {
        const accountNodes = buildAccountNodes(
          filteredAccounts.filter((acc) => acc.company === company.id),
          index
        );
        if (accountNodes.length === 0) return;

        companyMap.set(company.id, {
          id: company.id,
          label: company.company_name,
          type: 'company',
          children: accountNodes,
          index,
        });
      });

      // Accounts with no company (common for newly synced platforms like OpenAI Ads)
      // are otherwise invisible under company grouping.
      if (companyFilter.length === 0) {
        const unassignedAccounts = filteredAccounts.filter((acc) => !acc.company);
        const unassignedNodes = buildAccountNodes(unassignedAccounts, filteredCompanies.length);
        if (unassignedNodes.length > 0) {
          companyMap.set('__unassigned__', {
            id: '__unassigned__',
            label: 'Unassigned',
            type: 'company',
            children: unassignedNodes,
            index: filteredCompanies.length,
          });
        }
      }

      builtTree = Array.from(companyMap.values());
    } else {
      // Just account -> campaign (no company grouping)
      builtTree = filteredAccounts.map((account, index) => {
        const accountCampaigns = filteredCampaigns.filter(
          (camp) => camp.account?.id === account.id
        );
        return {
          id: account.ext_account_id,
          label: account.title,
          type: 'account',
          source: account.source,
          index: index,
          children: accountCampaigns.map(campaign => ({
            id: campaign.ext_campaign_id,
            label: campaign.title,
            type: 'campaign',
            source: account.source,
            accountId: account.ext_account_id,
            index: index,
          })),
        };
      }).filter((accountNode) => (accountNode.children?.length ?? 0) > 0);
    }

    // Apply search filter
    return filterTreeNodes(builtTree);
  }, [accounts, campaigns, companies, showCompanyGrouping, searchValue, accountFilter, companyFilter]);

  // Get checkbox state for a node
  const getCheckboxState = (node: TreeNode): CheckboxState => {
    if (node.type === 'campaign') {
      return localSelectedCampaignIds.includes(node.id) ? 'checked' : 'unchecked';
    }

    if (node.type === 'account') {
      const accountCampaigns = node.children || [];
      if (accountCampaigns.length === 0) {
        return localSelectedAccountIds.includes(node.id) ? 'checked' : 'unchecked';
      }

      const selectedCampaigns = accountCampaigns.filter(c => localSelectedCampaignIds.includes(c.id));
      if (selectedCampaigns.length === 0) {
        return 'unchecked';
      }
      if (selectedCampaigns.length === accountCampaigns.length) {
        return 'checked';
      }
      return 'indeterminate';
    }

    if (node.type === 'company') {
      const allAccountCampaigns = node.children?.flatMap(acc => acc.children || []) || [];
      if (allAccountCampaigns.length === 0) {
        const allAccountsSelected = node.children?.every(acc => localSelectedAccountIds.includes(acc.id)) || false;
        return allAccountsSelected ? 'checked' : 'unchecked';
      }

      const selectedCampaigns = allAccountCampaigns.filter(c => localSelectedCampaignIds.includes(c.id));
      if (selectedCampaigns.length === 0) {
        return 'unchecked';
      }
      if (selectedCampaigns.length === allAccountCampaigns.length) {
        return 'checked';
      }
      return 'indeterminate';
    }

    return 'unchecked';
  };

  const getNodeSelectionState = (node: TreeNode): boolean => {
    if (node.type === 'campaign') {
      return localSelectedCampaignIds.includes(node.id);
    }

    if (node.type === 'account') {
      const accountCampaigns = node.children || [];
      if (accountCampaigns.length === 0) {
        return localSelectedAccountIds.includes(node.id);
      }

      const selectedCampaigns = accountCampaigns.filter(c => localSelectedCampaignIds.includes(c.id));
      if (selectedCampaigns.length === 0) {
        return false;
      }
      if (selectedCampaigns.length === accountCampaigns.length) {
        return true;
      }
      return false;
    }

    if (node.type === 'company') {
      const allAccountCampaigns = node.children?.flatMap(acc => acc.children || []) || [];
      if (allAccountCampaigns.length === 0) {
        const allAccountsSelected = node.children?.every(acc => localSelectedAccountIds.includes(acc.id)) || false;
        return allAccountsSelected;
      }

      const selectedCampaigns = allAccountCampaigns.filter(c => localSelectedCampaignIds.includes(c.id));
      if (selectedCampaigns.length === 0) {
        return false;
      }
      if (selectedCampaigns.length === allAccountCampaigns.length) {
        return true;
      }
      return false;
    }

    return false;
  };

  // Handle node selection
  const handleNodeToggle = (node: TreeNode) => {
    if (disabled) return;

    if (node.type === 'campaign') {
      // Toggle campaign
      const newCampaignIds = localSelectedCampaignIds.includes(node.id)
        ? localSelectedCampaignIds.filter(id => id !== node.id)
        : [...localSelectedCampaignIds, node.id];

      setLocalSelectedCampaignIds(newCampaignIds);

      // If all campaigns for an account are selected, auto-select the account
      if (node.accountId) {
        // node.accountId is ext_account_id, find account by ext_account_id
        const account = accounts.find(a => a.ext_account_id === node.accountId);
        if (account) {
          const accountCampaigns = campaigns.filter((c) => c.account?.id === account.id);
          // Compare using ext_campaign_id since newCampaignIds contains ext_campaign_id values
          const allSelected = accountCampaigns.every(c => newCampaignIds.includes(c.ext_campaign_id));
          const newAccountIds = allSelected
            ? [...new Set([...localSelectedAccountIds, account.ext_account_id])]
            : localSelectedAccountIds.filter(id => id !== account.ext_account_id);

          setLocalSelectedAccountIds(newAccountIds);
          onSelectionChange(newAccountIds, newCampaignIds);
          return;
        }
      }

      onSelectionChange(localSelectedAccountIds, newCampaignIds);
    } else if (node.type === 'account') {
      // Toggle account - when selected, select all its campaigns by default
      const accountCampaigns = node.children || [];
      const accountCampaignIds = accountCampaigns.map(c => c.id);

      const isCurrentlySelected = localSelectedAccountIds.includes(node.id);

      if (isCurrentlySelected) {
        // Deselect account and all its campaigns
        const newAccountIds = localSelectedAccountIds.filter(id => id !== node.id);
        const newCampaignIds = localSelectedCampaignIds.filter(id => !accountCampaignIds.includes(id));
        setLocalSelectedAccountIds(newAccountIds);
        setLocalSelectedCampaignIds(newCampaignIds);
        onSelectionChange(newAccountIds, newCampaignIds);
      } else {
        // Select account and all its campaigns
        const newAccountIds = [...localSelectedAccountIds, node.id];
        const newCampaignIds = [...new Set([...localSelectedCampaignIds, ...accountCampaignIds])];
        setLocalSelectedAccountIds(newAccountIds);
        setLocalSelectedCampaignIds(newCampaignIds);
        onSelectionChange(newAccountIds, newCampaignIds);
      }
    } else if (node.type === 'company') {
      // Toggle company - select/deselect all accounts and campaigns under it
      const allAccounts = node.children || [];
      const allCampaigns = allAccounts.flatMap(acc => acc.children || []);
      const allAccountIds = allAccounts.map(acc => acc.id);
      const allCampaignIds = allCampaigns.map(c => c.id);

      const isCurrentlySelected = getCheckboxState(node) === 'checked';

      if (isCurrentlySelected) {
        // Deselect all
        const newAccountIds = localSelectedAccountIds.filter(id => !allAccountIds.includes(id));
        const newCampaignIds = localSelectedCampaignIds.filter(id => !allCampaignIds.includes(id));
        setLocalSelectedAccountIds(newAccountIds);
        setLocalSelectedCampaignIds(newCampaignIds);
        onSelectionChange(newAccountIds, newCampaignIds);
      } else {
        // Select all
        const newAccountIds = [...new Set([...localSelectedAccountIds, ...allAccountIds])];
        const newCampaignIds = [...new Set([...localSelectedCampaignIds, ...allCampaignIds])];
        setLocalSelectedAccountIds(newAccountIds);
        setLocalSelectedCampaignIds(newCampaignIds);
        onSelectionChange(newAccountIds, newCampaignIds);
      }
    }
  };

  const getNodeTextStyle = (node: TreeNode) => {
    if (node.type === 'company') {
      return 'text-base font-semibold tracking-[-0.2px] text-[#0D0F1A]';
    }
    if (node.type === 'account') {
      return 'text-[13.5px] font-semibold text-[#0D0F1A]';
    }
    return 'text-[13px] text-[#0D0F1A]';
  };

  const campaignCountLabel = (node: TreeNode) => {
    if (node.type === 'company') {
      const count = node.children?.reduce((sum, account) => sum + (account.children?.length ?? 0), 0) ?? 0;
      return `${count} campaign${count === 1 ? '' : 's'}`;
    }
    if (node.type === 'account') {
      const count = node.children?.length ?? 0;
      return `${count}`;
    }
    return '';
  };

  const renderTreeNode = (node: TreeNode, level: number = 0) => {
    const isCompany = node.type === 'company';
    const isExpanded = !isCompany || expandedCompanyIds.includes(node.id) || expandedCompanyIds.length === 0;
    const checkboxState = getCheckboxState(node);
    const paddingLeft = isCompany ? 16 : level === 1 ? 40 : 64;

    return (
      <div
        key={node.id}
        className={cn(isCompany && 'border-b border-[#EDEEF2] last:border-b-0')}
      >
        <div
          className="flex items-center gap-2.5"
          style={{
            paddingLeft,
            paddingRight: 16,
            paddingTop: isCompany ? 12 : 8,
            paddingBottom: isCompany ? 12 : 8,
            background: isCompany ? '#F7F8FA' : undefined,
          }}
        >
          <SelectionCheckbox
            state={checkboxState}
            disabled={disabled}
            onClick={() => handleNodeToggle(node)}
          />
          {isCompany ? (
            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-2 text-left"
              onClick={() => {
                setExpandedCompanyIds((current) => {
                  const allIds = treeData.map((item) => item.id);
                  const expanded = current.length === 0 ? allIds : current;
                  return expanded.includes(node.id)
                    ? expanded.filter((id) => id !== node.id)
                    : [...expanded, node.id];
                });
              }}
            >
              {isExpanded ? (
                <ChevronDown className="size-[15px] shrink-0 text-[#9CA3AF]" />
              ) : (
                <ChevronRight className="size-[15px] shrink-0 text-[#9CA3AF]" />
              )}
              <span className={getNodeTextStyle(node)}>
                {node.label}
              </span>
            </button>
          ) : (
            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-2 text-left"
              disabled={disabled}
              onClick={() => handleNodeToggle(node)}
            >
              <span className={getNodeTextStyle(node)}>
                {node.label}
                {node.source ? (
                  <span className={cn(portalDs.caption, 'ml-1 font-normal')}>
                    (
                    {node.source.replace('_', ' ').toLowerCase()}
                    )
                  </span>
                ) : null}
              </span>
            </button>
          )}
          {isCompany || node.type === 'account' ? (
            <span className={portalDs.caption}>
              {campaignCountLabel(node)}
            </span>
          ) : null}
        </div>
        {node.children && node.children.length > 0 && isExpanded ? (
          <div>
            {node.children.map((child) => renderTreeNode(child, level + 1))}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div
      className="overflow-hidden rounded-xl border border-[#EDEEF2]"
      aria-busy={isLoading}
      aria-live="polite"
    >
      {isLoading ? (
        <div
          className="px-3 py-2"
          role="status"
          aria-label="Loading accounts and campaigns"
        >
          {[0, 1, 2, 3, 4, 5].map((row) => (
            <div
              key={row}
              className="flex items-center gap-3 py-2.5"
              style={{ paddingLeft: row === 0 || row === 3 ? 8 : 28 }}
            >
              <Skeleton className="size-4 shrink-0 rounded" />
              <Skeleton className={cn('h-3.5', row % 3 === 0 ? 'w-40' : 'w-56')} />
              {row === 0 || row === 3 ? (
                <Skeleton className="ml-auto h-3 w-16" />
              ) : null}
            </div>
          ))}
        </div>
      ) : treeData.length === 0 ? (
        <div className={cn(portalDs.tableCellMuted, 'p-4 text-center text-[13.5px]')}>
          No accounts or campaigns available
        </div>
      ) : (
        treeData.map((node) => renderTreeNode(node as TreeNode))
      )}
    </div>
  );
};

export default ManageClientAccountCampaignSelection;
